import { ChangeDetectorRef, Component, inject, OnInit, signal, ViewChild, computed, ChangeDetectionStrategy } from '@angular/core';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators, FormsModule, } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { DropdownComponent, DropdownOption, } from '../../../../shared/components/ui/dropdown/dropdown.component';
import { ButtonComponent } from '../../../../shared/components/ui/button/button.component';
import { ApiService } from '../../../../core/services/api.service';
import { PaginatedTableComponent, TableHeader, } from '../../../../shared/components/tables/paginated-table/paginated-table.component';
import { AlertComponent } from '../../../../shared/components/alert/alert.component';
import { IconComponent, IconName, } from '../../../../shared/icons/icon.component';
import { ToasterService } from '../../../../core/services/toaster.service';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ConfirmationModalComponent } from '../../../../shared/components/confirmation-modal/confirmation-modal.component';
import { SlidePanelComponent } from '../../../../shared/components/slide-panel/slide-panel.component';
import { InputComponent } from '../../../../shared/components/ui/input/input.component';
import { AuditLogPanelComponent } from '../../../../shared/components/audit-log-panel/audit-log-panel.component';
import { AuditEntityType } from '../../../../core/models/audit-log.model';

export enum RoleScope {
  ALL_PRODUCTS = 'ALL_PRODUCTS',
  LIMITED_PRODUCTS = 'LIMITED_PRODUCTS',
}

interface Role {
  id: string;
  name: string;
  description?: string;
  isSystem: boolean;
  productScope: RoleScope;
  isActive: boolean;
  status: string;
  createdAt: string;
  updatedAt: string;
  RoleProducts?: any[];
}

interface RoleApiResponse {
  roles: Role[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

@Component({
  selector: 'app-roles',
  imports: [
    ReactiveFormsModule,
    FormsModule,
    CommonModule,
    DropdownComponent,
    ButtonComponent,
    PaginatedTableComponent,
    // AlertComponent,
    SlidePanelComponent,
    InputComponent,
    IconComponent,
    AuditLogPanelComponent,
  ],
  standalone: true,
  templateUrl: './roles.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      .custom-scrollbar::-webkit-scrollbar {
        width: 5px;
      }
      .custom-scrollbar::-webkit-scrollbar-track {
        background: transparent;
      }
      .custom-scrollbar::-webkit-scrollbar-thumb {
        background: rgba(139, 92, 246, 0.2);
        border-radius: 10px;
      }
      .custom-scrollbar::-webkit-scrollbar-thumb:hover {
        background: rgba(139, 92, 246, 0.4);
      }
    `,
  ],
})
export class RolesComponent implements OnInit {
  @ViewChild(AuditLogPanelComponent) auditLogPanel!: AuditLogPanelComponent;
  private fb = inject(FormBuilder);
  private toasterService = inject(ToasterService);
  private dialog = inject(MatDialog);
  private apiService = inject(ApiService);
  private cdr = inject(ChangeDetectorRef);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  // States
  panelOpen = signal(false);
  isLoading = signal(false);
  formSubmitted = signal(false);
  totalItems = signal(0);
  isEditing = signal(false);
  editingId = signal<string | null>(null);

  // Filters
  searchTerm = signal('');
  selectedStatus = signal<string | null>(null);
  page = signal(1);
  pageSize = signal(10);
  sortBy = signal('createdAt');
  sortOrder = signal('desc');
  roleData = signal<Role[]>([]);

  auditEntityTypes = [AuditEntityType.USER_ROLE];

  // Product Access State
  allProducts = signal<any[]>([]);
  productSearchTerm = signal('');

  // Form
  form: FormGroup;

  // Table Headers
  tableHeaders: TableHeader[] = [
    { label: 'Role Name', key: 'name', align: 'text-left' },
    { label: 'Description', key: 'description', align: 'text-left' },
    { label: 'Status', key: 'statusLabel', align: 'text-left' },
    {
      label: 'Created Date',
      key: 'createdAt',
      align: 'text-left',
      dateFormat: 'dashDate',
    },
  ];

  tableActionOptions = [
    {
      label: 'Edit',
      value: 'edit',
      iconName: 'squarePen' as IconName,
      isShow: true,
      visibilityKey: 'canEdit',
      color: '#1E6AFD',
    },
    {
      label: 'Delete',
      value: 'delete',
      iconName: 'trash' as IconName,
      isShow: true,
      visibilityKey: 'canDelete',
      color: 'red',
    },
    {
      label: 'Restore',
      value: 'restore',
      iconName: 'rotateCcw' as IconName,
      isShow: true,
      visibilityKey: 'canRestore',
      color: '#AD46FF',
    },
  ];

  statusOptions: DropdownOption[] = [
    { label: 'Active', value: 'ACTIVE' },
    { label: 'Suspended', value: 'SUSPENDED' },
  ];

  // formStatusOptions: DropdownOption[] = [
  //   { label: 'Active', value: 'ACTIVE' },
  //   { label: 'Suspended', value: 'SUSPENDED' },
  // ];

  constructor() {
    this.form = this.fb.group({
      name: [
        '',
        [
          Validators.required,
          Validators.minLength(3),
          Validators.maxLength(50),
          Validators.pattern(/^[A-Z_]*$/),
        ],
      ],
      description: ['', [Validators.maxLength(150)]],
      productScope: ['ALL_PRODUCTS', [Validators.required]],
      productIds: [[]],
      status: ['ACTIVE', [Validators.required]],
      isSystem: [false],
    });
  }

  filteredProducts = computed(() => {
    const term = this.productSearchTerm().toLowerCase();
    const products = this.allProducts();
    if (!term) return products;
    return products.filter((p) =>
      (p.name || p.displayName || p.label)?.toLowerCase().includes(term),
    );
  });

  selectedCount = computed(() => {
    const selectedIds = this.form.get('productIds')?.value || [];
    return selectedIds.length;
  });

  isProductSelected(product: any): boolean {
    const selectedIds = this.form.get('productIds')?.value || [];
    return (
      selectedIds.includes(product.id) ||
      (product.pid && selectedIds.includes(product.pid))
    );
  }

  isAllProductsSelected(): boolean {
    const selectedIds = this.form.get('productIds')?.value || [];
    const allIds = this.allProducts().map((p) => p.id);
    return (
      allIds.length > 0 &&
      this.allProducts().every((p) => selectedIds.includes(p.id))
    );
  }

  onProductToggle(product: any): void {
    const control = this.form.get('productIds');
    const selectedIds = [...(control?.value || [])];

    const indexId = selectedIds.indexOf(product.id);
    const indexPid = product.pid ? selectedIds.indexOf(product.pid) : -1;

    if (indexId > -1) {
      selectedIds.splice(indexId, 1);
    } else {
      selectedIds.push(product.id);
    }

    control?.setValue(selectedIds);
    this.cdr.markForCheck();
  }

  toggleAllProductsLocal(): void {
    const isAllSelected = this.isAllProductsSelected();
    const control = this.form.get('productIds');

    if (!isAllSelected) {
      control?.setValue(this.allProducts().map((p) => p.id));
    } else {
      control?.setValue([]);
    }
    this.cdr.markForCheck();
  }

  onInputChange(controlName: string): void {
    const control = this.form.get(controlName);
    if (control) {
      control.markAsUntouched();
      control.markAsPristine();
    }
  }

  get processedRoleData() {
    return this.roleData().map((role) => ({
      ...role,
      statusLabel:
        role.status === 'DELETED'
          ? 'Deleted'
          : role.isActive
            ? 'Active'
            : 'Suspended',
      canEdit: role.isActive && role.status !== 'DELETED',
      canDelete: role.isActive && role.status !== 'DELETED',
      canRestore: !role.isActive || role.status === 'DELETED',
      isDeleted: role.status === 'DELETED',
      isNotDeleted: role.status !== 'DELETED',
    }));
  }

  ngOnInit() {
    this.fetchRoles();
    this.loadProducts();
  }

  loadProducts() {
    this.apiService.fetchProducts().subscribe({
      next: (products) => {
        const uniqueProducts = Array.from(
          new Map(products.map((p) => [p.id, p])).values(),
        );
        this.allProducts.set(uniqueProducts);
      },
    });
  }

  fetchRoles(): void {
    this.isLoading.set(true);
    const params: any = {
      page: this.page(),
      limit: this.pageSize(),
      sortBy: this.sortBy(),
      sortOrder: this.sortOrder(),
      includeProducts: true,
    };

    if (this.selectedStatus()) params.status = this.selectedStatus();
    if (this.searchTerm()) params.search = this.searchTerm();

    this.apiService
      .get('/roles/list', params, {
        showLoader: true,
        showErrorToaster: true,
      })
      .subscribe({
        next: (response: any) => {
          // The API structure from existing code says response might have .roles or be the array
          if (response?.roles && Array.isArray(response.roles)) {
            this.roleData.set(response.roles);
            this.totalItems.set(
              response.total ?? (response.pagination?.total || response.roles.length),
            );
          } else if (Array.isArray(response)) {
            this.roleData.set(response);
            this.totalItems.set(response.length);
          } else if (response?.data && Array.isArray(response.data)) {
            this.roleData.set(response.data);
            this.totalItems.set(
              response.total ?? (response.data.total ?? (response.pagination?.total || response.data.length)),
            );
          }
          this.isLoading.set(false);
          this.cdr.markForCheck();
        },
        error: (error) => {
          this.isLoading.set(false);
        },
      });
  }

  // Panel Methods
  openPanel(role?: any) {
    if (role) {
      this.isEditing.set(true);
      this.editingId.set(role.id);

      const roleProducts = role.roleProducts || role.RoleProducts || [];
      const productIds =
        roleProducts.map(
          (rp: any) => rp.productId || rp.product?.id || rp.id,
        ) || [];

      this.form.patchValue({
        ...role,
        status:
          role.status === 'DELETED'
            ? 'DELETED'
            : role.isActive
              ? 'ACTIVE'
              : 'SUSPENDED',
        productIds: productIds,
      });

      if (role.productScope) {
        this.form.patchValue({ productScope: role.productScope });
      }
    } else {
      this.isEditing.set(false);
      this.editingId.set(null);
      this.form.reset({
        status: 'ACTIVE',
        productScope: 'ALL_PRODUCTS',
        productIds: this.allProducts().map((p) => p.id),
        isSystem: false,
      });
    }
    this.panelOpen.set(true);
  }

  closePanel() {
    this.panelOpen.set(false);
    this.formSubmitted.set(false);
    this.form.reset({
      status: 'ACTIVE',
      productScope: 'ALL_PRODUCTS',
      productIds: [],
      isSystem: false,
    });
  }

  submitForm() {
    this.formSubmitted.set(true);
    if (this.form.invalid) return;

    this.isLoading.set(true);
    const formValue = this.form.getRawValue();

    const payload: any = {
      ...formValue,
      name: formValue.name?.trim().toUpperCase(),
      status: formValue.status.toUpperCase(),
      isActive: formValue.status === 'ACTIVE',
      productScope: formValue.productScope || 'ALL_PRODUCTS',
      productIds:
        formValue.productScope === 'ALL_PRODUCTS'
          ? this.allProducts().map((p) => p.id)
          : formValue.productIds || [],
    };

    if (this.isEditing()) {
      payload.id = this.editingId();
    }

    const endpoint = this.isEditing()
      ? `/roles/${this.editingId()}`
      : '/roles/create';
    const request = this.isEditing()
      ? this.apiService.put(endpoint, payload, { showErrorToaster: true })
      : this.apiService.post(endpoint, payload, { showErrorToaster: true });

    request.subscribe({
      next: (response: any) => {
        this.isLoading.set(false);
        const isCreating = !this.isEditing();

        // Extract role details from response or data key
        const roleData = response?.data || response;
        const roleId = roleData?.id;
        const roleName = roleData?.name || formValue.name?.trim().toUpperCase();

        if (isCreating && roleId) {
          // Navigating to permissions page (Image 2) using relative path from current route
          this.router.navigate([`/app/prism/menu-roles/manage-roles/${roleId}`], { queryParams: { role: roleName } });
        }

        this.toasterService.success(
          `Role ${!isCreating ? 'updated' : 'created'} successfully`,
        );

        this.closePanel();
        this.fetchRoles();
        this.auditLogPanel?.reloadLogs();
      },
      error: (err) => {
        this.isLoading.set(false);
      },
    });
  }

  // Filter Handlers
  onStatusFilter(status: string | string[] | null): void {
    const selectedId = Array.isArray(status) ? status[0] : status;
    this.selectedStatus.set(selectedId === '' ? null : selectedId);
  }

  onSearch(term: string): void {
    this.searchTerm.set(term);
  }

  applyFilters() {
    this.page.set(1);
    this.fetchRoles();
  }

  onPageChange(page: number): void {
    this.page.set(page);
    this.fetchRoles();
  }

  clearFilters() {
    this.selectedStatus.set(null);
    this.searchTerm.set('');
    this.page.set(1);
    this.fetchRoles();
  }

  // Table Event Handlers
  selectedRowItem(item: any) {
    if (!item || !item.row) return;
    if (item.action === 'edit') {
      this.openPanel(item.row);
    } else if (item.action === 'delete') {
      this.checkUsersBeforeDelete(item.row);
    } else if (item.action === 'restore') {
      this.handleRestoreRole(item.row.id);
    }
  }

  checkUsersBeforeDelete(row: any) {
    this.apiService
      .get(`/roles/${row.id}/users`, {}, { showLoader: true, showErrorToaster: true })
      .subscribe({
        next: (res: any) => {
          const hasUsers = res?.data?.length > 0;
          this.openConfirmationDialog(row, hasUsers);
        },
        error: (err) => {
        },
      });
  }

  openConfirmationDialog(row: any, hasUsers: boolean = false) {
    let description = `Are you sure you want to delete the role "${row.name}"?`;

    if (hasUsers) {
      description = `Warning: The role "${row.name}" is already assigned to users. If you delete (suspend) this role, these users will lose access to their accounts. Are you sure you want to proceed?`;
    }

    this.dialog
      .open(ConfirmationModalComponent, {
        width: '450px',
        data: {
          title: hasUsers ? 'Warning: Role Assigned' : 'Confirm Deletion',
          description: description,
        },
      })
      .afterClosed()
      .subscribe((res) => {
        if (res) this.handleRemoveRole(row.id);
      });
  }

  handleRemoveRole(id: string) {
    this.apiService
      .delete(`/roles/${id}`, {
        showErrorToaster: true,
        showLoader: true,
      })
      .subscribe({
        next: () => {
          this.toasterService.success('Role deleted successfully');
          this.fetchRoles();
          this.auditLogPanel?.reloadLogs();
        },
        error: (error) => {
        },
      });
  }

  handleRestoreRole(id: string) {
    this.apiService
      .post(
        `/roles/${id}/restore`,
        {},
        {
          showErrorToaster: true,
          showLoader: true,
        },
      )
      .subscribe({
        next: () => {
          this.toasterService.success('Role restored successfully');
          this.fetchRoles();
          this.auditLogPanel?.reloadLogs();
        },
        error: (error) => {
        },
      });
  }

  onNameInput(event: any): void {
    const target = event.target as HTMLInputElement;
    // Replace anything that is not a letter or underscore
    const value = target.value.toUpperCase().replace(/[^A-Z_]/g, '');
    this.form.get('name')?.setValue(value, { emitEvent: false });
    target.value = value;
  }

  preventLeadingSpace(event: KeyboardEvent): void {
    const target = event.target as HTMLInputElement;
    if (event.key === ' ' && target.selectionStart === 0) {
      event.preventDefault();
    }
  }

  getErrorMessage(controlName: string): string | undefined {
    const control = this.form.get(controlName);
    if (
      !control ||
      (!control.touched && !this.formSubmitted()) ||
      control.valid
    )
      return undefined;

    if (control.hasError('required')) return 'This field is required';
    if (control.hasError('minlength')) {
      const required = control.errors?.['minlength']?.requiredLength;
      return `Minimum ${required} characters required`;
    }
    if (control.hasError('maxlength')) {
      const required = control.errors?.['maxlength']?.requiredLength;
      return `Maximum ${required} characters allowed`;
    }
    if (control.hasError('pattern')) {
      return 'Only alphabets and underscores are allowed';
    }
    return 'Invalid field';
  }
}
